import { faCakeCandles } from "@fortawesome/free-solid-svg-icons/faCakeCandles";
import { faThumbsDown } from "@fortawesome/free-solid-svg-icons/faThumbsDown";
import { faThumbsUp } from "@fortawesome/free-solid-svg-icons/faThumbsUp";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useContext, useEffect } from "react";
import { AppContext } from "~/contexts/AppContext";
import { faCircleQuestion } from "@fortawesome/free-solid-svg-icons/faCircleQuestion";
import StyledDropDown from "~/components/StyledDropdown";
import { faMedal } from "@fortawesome/free-solid-svg-icons/faMedal";
import { useTranslation } from "react-i18next";
import { fixBold } from "~/util/util";
import { GroupResult } from "~/types";
import TypingTextV2 from "~/components/TypingTextV2";

interface AgeResultSlideProps {
    groupResult: GroupResult;
    ratingValue: number;
    submitBirthYear: React.ChangeEventHandler;
    fadeInComplete: boolean;
    onAnimationComplete: () => void;
}

const AgeResultSlide: React.FC<AgeResultSlideProps> = (props) => {
    const { profile } = useContext(AppContext)!;
    const { t } = useTranslation();
    
    if(!profile) return null;

    useEffect(() => {
        if (!profile.birthYear && props.fadeInComplete) {
            props.onAnimationComplete();
        }
    }, [props.fadeInComplete, profile.birthYear]);

    if (!profile.birthYear || !props.groupResult)
        return (
            <>
                <span className='header-icon-row'>
                    <span className='icon'><FontAwesomeIcon icon={faCakeCandles} /></span>
                    <span className='icon'><FontAwesomeIcon icon={faCircleQuestion} /></span>
                </span>
                <p style={{ fontSize: '16px', marginBottom: '10px' }}>{t('results_age_input_let_me_show_you')}</p>
                <StyledDropDown style={{ fontSize: '16px', padding: '17px 20px', marginLeft: '-6px', width: 'calc(100% + 12px)' }} onChange={props.submitBirthYear} value={t('input_placeholder_birth_year')}>
                    <option disabled>{t('input_placeholder_birth_year')}</option>
                    {Array.from(Array(74).keys()).map(n => 2008 - n).map(year =>
                        <option key={year} value={year}>{year}</option>
                    )}
                </StyledDropDown>
                <p style={{ fontSize: '12px', marginTop: '10px' }}>{t('results_age_input_disclaimer')}</p>
            </>
        );

    if (props.groupResult.firstRating) {
        return (
            <>
                <span className='header-icon-row'>
                    <span className='icon'><FontAwesomeIcon icon={faCakeCandles} /></span>
                    <span className='icon'><FontAwesomeIcon icon={faMedal} /></span>
                </span>
                <TypingTextV2 active={props.fadeInComplete} delay={10} onComplete={props.onAnimationComplete}>
                    <p>
                        {fixBold(t('results_age_first_rating'))}
                    </p>
                </TypingTextV2>
            </>
        );
    }

    if (props.groupResult.rating === null)
        return null;

    const averageRating: number = props.groupResult.rating;
    const userRating = props.ratingValue;

    const ratingsDiff = Math.abs(userRating - averageRating);

    let agreementMessage;
    let agreementIcon;

    if(ratingsDiff <= 15) {
        agreementIcon = faThumbsUp;
        agreementMessage = fixBold(t('results_age_strong_agreement'));
    } else if (ratingsDiff <= 30) {
        agreementIcon = faThumbsUp;
        agreementMessage = fixBold(t('results_age_weak_agreement'));
    } else if(ratingsDiff <= 50) {
        agreementIcon = faThumbsDown;
        agreementMessage = fixBold(t('results_age_weak_disagreement'));
    } else {
        agreementIcon = faThumbsDown;
        agreementMessage = fixBold(t('results_age_strong_disagreement'));
    }

    return (
        <>
            <span className='header-icon-row'>
                <span className='icon'><FontAwesomeIcon icon={faCakeCandles} /></span>
                <span className='icon'><FontAwesomeIcon icon={agreementIcon} /></span>
            </span>
            <TypingTextV2 active={props.fadeInComplete} delay={10} onComplete={props.onAnimationComplete}>
                <p>{agreementMessage}</p>
            </TypingTextV2>
        </>
    );
};

export default AgeResultSlide;